import React, { useState, useEffect, useCallback } from 'react';
import ModuleNav, { MODULE_NAVS } from '../ModuleNav';
import { 
  Card, Table, Button, Space, Tag, Badge, Input, Select, DatePicker, 
  Modal, message, Typography, Empty, Spin, Tooltip, Row, Col, Statistic
} from 'antd';
import { 
  ReloadOutlined, UndoOutlined, EyeOutlined, SearchOutlined,
  WarningOutlined, CheckCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons';
import lib from '../../assets/utils/lib';
import { notification } from 'antd';
import dayjs from 'dayjs';
import { SORT_BY, SORT_DIR, parseSortParams, sorterToQuery, toSortQuery, withServerSorter } from '../../utils/tableSortParams';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

/**
 * Soft-Deleted Checkins List Component
 * Allows admins to view, search, and restore soft-deleted checkins
 */
const SoftDeletedCheckinsList = () => {
  const [checkins, setCheckins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState({});
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    facility_id: null,
    delete_reason: null,
    dateRange: null,
  });
  const [selectedCheckin, setSelectedCheckin] = useState(null);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [listSort, setListSort] = useState({ sort_by: SORT_BY.CHECKOUT_DATE, sort_dir: SORT_DIR.DESC });

  // Fetch soft-deleted checkins
  const fetchSoftDeletedCheckins = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        pageSize: pagination.pageSize,
        deleted: 1,
        ...toSortQuery(listSort.sort_by, listSort.sort_dir),
      };

      // Add filters
      if (filters.search) {
        params.search = filters.search;
      }
      if (filters.facility_id) {
        params.facility_id = filters.facility_id;
      }
      if (filters.delete_reason) {
        params.delete_reason = filters.delete_reason;
      }
      if (filters.dateRange && filters.dateRange.length === 2) {
        params.date_from = filters.dateRange[0].format('YYYY-MM-DD');
        params.date_to = filters.dateRange[1].format('YYYY-MM-DD');
      }

      const response = await lib.getSoftDeletedCheckins(params);
      
      if (response?.data?.error) {
        notification.error({
          message: 'Error',
          description: response.data.message || 'Failed to fetch soft-deleted checkins',
        });
        return;
      }

      const data = response?.data?.data || [];
      const paginationData = response?.data?.pagination || {};

      setCheckins(Array.isArray(data) ? data : []);
      setPagination(prev => ({
        ...prev,
        current: paginationData.page || page,
        total: paginationData.total || 0,
      }));
    } catch (error) {
      console.error('Error fetching soft-deleted checkins:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to fetch soft-deleted checkins',
      });
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize, filters, listSort]);

  useEffect(() => {
    fetchSoftDeletedCheckins(1);
  }, [filters, pagination.pageSize, fetchSoftDeletedCheckins]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore checkin
  const handleRestore = async (checkinId) => {
    Modal.confirm({
      title: 'Restore Checkin',
      content: `Restore checkin ${checkinId}? It comes back on the Encounters page as checked out, awaiting claim submission.`,
      okText: 'Yes, Restore',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        setRestoring(prev => ({ ...prev, [checkinId]: true }));
        try {
          const response = await lib.restoreCheckin(checkinId);
          
          if (response?.data?.error) {
            notification.error({
              message: 'Restore Failed',
              description: response.data.message || 'Failed to restore checkin',
            });
          } else {
            notification.success({
              message: 'Checkin Restored',
              description: `Checkin ${checkinId} has been restored successfully`,
            });
            // Refresh list
            fetchSoftDeletedCheckins(pagination.current);
          }
        } catch (error) {
          console.error('Error restoring checkin:', error);
          notification.error({
            message: 'Error',
            description: 'Failed to restore checkin',
          });
        } finally {
          setRestoring(prev => ({ ...prev, [checkinId]: false }));
        }
      },
    });
  };

  // View checkin details
  const handleViewDetails = (checkin) => {
    setSelectedCheckin(checkin);
    setDetailsModalVisible(true);
  };

  // Handle pagination
  const handleTableChange = (pager, _tableFilters, sorter) => {
    const nextSort = sorterToQuery(sorter);
    if (nextSort.sort_by && (nextSort.sort_by !== listSort.sort_by || nextSort.sort_dir !== listSort.sort_dir)) {
      setListSort(nextSort);
      setPagination((prev) => ({ ...prev, current: 1, pageSize: pager?.pageSize || prev.pageSize }));
      return;
    }
    const page = pager?.current || 1;
    const pageSize = pager?.pageSize || pagination.pageSize;
    setPagination((prev) => ({ ...prev, current: page, pageSize }));
    fetchSoftDeletedCheckins(page);
  };

  // Get delete reason badge
  const getDeleteReasonBadge = (reason) => {
    const reasonMap = {
      'no_pa_items': { color: 'error', text: 'No PA Items' },
      'no_approved_pa_items': { color: 'warning', text: 'No Approved PA Items' },
    };
    const config = reasonMap[reason] || { color: 'default', text: reason || 'Unknown' };
    return <Badge status={config.color} text={config.text} />;
  };

  const columns = [
    {
      title: 'Checkin ID',
      dataIndex: 'code',
      key: 'code',
      width: 180,
      render: (text) => <Text strong copyable>{text}</Text>,
    },
    {
      title: 'Facility',
      dataIndex: 'facility_name',
      key: 'facility_name',
      width: 200,
    },
    {
      title: 'Enrollee',
      key: 'enrollee',
      width: 200,
      render: (_, record) => (
        <div>
          <div>{record.first_name} {record.surname}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.insurance_no}
          </Text>
        </div>
      ),
    },
    withServerSorter(
      {
        title: 'Checkout Date',
        dataIndex: 'checkout_date',
        key: SORT_BY.CHECKOUT_DATE,
        width: 150,
        render: (date) => date ? dayjs(date).format('MMM D, YYYY HH:mm') : 'N/A',
      },
      SORT_BY.CHECKOUT_DATE,
      listSort,
    ),
    {
      title: 'Delete Reason',
      dataIndex: 'delete_reason',
      key: 'delete_reason',
      width: 180,
      render: (reason) => getDeleteReasonBadge(reason),
    },
    withServerSorter(
      {
        title: 'PA Items',
        key: SORT_BY.ITEMS,
        width: 120,
        render: (_, record) => (
          <div>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.approved_items_count || 0} / {record.pa_items_count || 0} approved
            </Text>
          </div>
        ),
      },
      SORT_BY.ITEMS,
      listSort,
    ),
    withServerSorter(
      {
        title: 'Cost',
        key: SORT_BY.AMOUNT,
        width: 110,
        align: 'right',
        render: (_, record) => {
          const n = Number(record.requested_total);
          return Number.isFinite(n) ? `₦${n.toLocaleString()}` : '—';
        },
      },
      SORT_BY.AMOUNT,
      listSort,
    ),
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          <Tooltip title="Restore Checkin">
            <Button
              type="primary"
              icon={<UndoOutlined />}
              size="small"
              loading={restoring[record.code]}
              onClick={() => handleRestore(record.code)}
            >
              Restore
            </Button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '24px' }}>
          <Title level={4} style={{ marginBottom: '16px' }}>
            <WarningOutlined style={{ marginRight: '8px', color: '#faad14' }} />
            Soft-Deleted Checkins
          </Title>
          <ModuleNav items={MODULE_NAVS.encounters} />
          
          {/* Filters */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Input
                placeholder="Search by checkin ID, enrollee name, or insurance number"
                prefix={<SearchOutlined />}
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Delete Reason"
                style={{ width: '100%' }}
                value={filters.delete_reason}
                onChange={(value) => setFilters(prev => ({ ...prev, delete_reason: value }))}
                allowClear
              >
                <Select.Option value="no_pa_items">No PA Items</Select.Option>
                <Select.Option value="no_approved_pa_items">No Approved PA Items</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <RangePicker
                style={{ width: '100%' }}
                value={filters.dateRange}
                onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates }))}
                format="MMM D, YYYY"
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => fetchSoftDeletedCheckins(pagination.current)}
                loading={loading}
                block
              >
                Refresh
              </Button>
            </Col>
          </Row>
        </div>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={checkins}
          rowKey="code"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} soft-deleted checkins`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
          locale={{
            emptyText: (
              <Empty
                description="No soft-deleted checkins found"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            ),
          }}
        />
      </Card>

      {/* Details Modal */}
      <Modal
        title={`Checkin Details: ${selectedCheckin?.code}`}
        open={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailsModalVisible(false)}>
            Close
          </Button>,
          selectedCheckin && (
            <Button
              key="restore"
              type="primary"
              icon={<UndoOutlined />}
              loading={restoring[selectedCheckin?.code]}
              onClick={() => {
                setDetailsModalVisible(false);
                handleRestore(selectedCheckin.code);
              }}
            >
              Restore Checkin
            </Button>
          ),
        ]}
        width={800}
      >
        {selectedCheckin && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic title="Checkin ID" value={selectedCheckin.code} />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Facility"
                  value={selectedCheckin.facility_name || 'N/A'}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Enrollee"
                  value={`${selectedCheckin.first_name || ''} ${selectedCheckin.surname || ''}`.trim() || 'N/A'}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Insurance Number"
                  value={selectedCheckin.insurance_no || 'N/A'}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Checkout Date"
                  value={selectedCheckin.checkout_date ? dayjs(selectedCheckin.checkout_date).format('MMM D, YYYY HH:mm') : 'N/A'}
                />
              </Col>
              <Col span={12}>
                <div>
                  <Text strong>Delete Reason: </Text>
                  {getDeleteReasonBadge(selectedCheckin.delete_reason)}
                </div>
              </Col>
              <Col span={12}>
                <Statistic
                  title="Total PA Items"
                  value={selectedCheckin.pa_items_count || 0}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Approved PA Items"
                  value={selectedCheckin.approved_items_count || 0}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default SoftDeletedCheckinsList;

